import { useEffect, useRef, useState } from "react";
import { Download, Upload, Loader2, FileJson, CheckCircle2, AlertTriangle, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "./api";

const COLLECTION_LABELS = {
  cms_sections: "Sections & settings",
  cms_programs: "Programs",
  cms_post_types: "Post types",
  cms_posts: "Posts",
};

const downloadJson = (filename, payload) => {
  const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

export const Snapshot = () => {
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [mode, setMode] = useState("replace"); // replace | merge
  const [staged, setStaged] = useState(null); // parsed JSON ready to import
  const [stageName, setStageName] = useState("");
  const [lastResult, setLastResult] = useState(null);
  const [currentCounts, setCurrentCounts] = useState(null);
  const fileRef = useRef(null);

  const loadCurrentCounts = async () => {
    try {
      const { data } = await api.get("/cms/snapshot/export");
      setCurrentCounts(data.counts);
    } catch {
      /* ignore */
    }
  };

  useEffect(() => {
    loadCurrentCounts();
  }, []);

  const onExport = async () => {
    try {
      setExporting(true);
      const { data } = await api.get("/cms/snapshot/export");
      const stamp = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
      downloadJson(`bdh-cms-snapshot-${stamp}.json`, data);
      toast.success("Snapshot downloaded");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Export failed");
    } finally {
      setExporting(false);
    }
  };

  const onFileChosen = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const text = await file.text();
      const parsed = JSON.parse(text);
      if (parsed?.version !== 1) {
        toast.error(`Unsupported snapshot version: ${parsed?.version}`);
        return;
      }
      setStaged(parsed);
      setStageName(file.name);
    } catch {
      toast.error("That doesn't look like a valid snapshot JSON file.");
    } finally {
      // Reset input so picking the same file again refires
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const onImport = async () => {
    if (!staged) return;
    const verb = mode === "replace" ? "REPLACE all CMS content" : "MERGE the snapshot into existing CMS content";
    if (!window.confirm(`This will ${verb}. Continue?`)) return;
    try {
      setImporting(true);
      const { data } = await api.post(`/cms/snapshot/import?mode=${mode}`, staged);
      setLastResult(data);
      setStaged(null);
      setStageName("");
      await loadCurrentCounts();
      toast.success(`Imported (${mode})`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Import failed");
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="space-y-8 max-w-[1100px]">
      <div>
        <div className="text-[10px] tracking-[0.3em] uppercase text-brand-light font-bold">CMS Tools</div>
        <h1 className="font-display uppercase text-3xl md:text-4xl mt-1">Content Snapshots</h1>
        <p className="text-sm text-zinc-400 mt-3 max-w-2xl leading-relaxed">
          Move CMS content between environments. Export from one environment, then import into another to mirror sections, programs, settings, post types, and posts. Inquiries, subscribers, users, and migration history are intentionally excluded.
        </p>
      </div>

      {/* Current environment summary */}
      <div className="bento-card p-6">
        <div className="flex items-center gap-3 mb-4">
          <FileJson className="h-4 w-4 text-brand-light" />
          <div className="text-[10px] tracking-[0.3em] uppercase font-bold text-brand-light">Current environment</div>
          <button onClick={loadCurrentCounts} className="ml-auto p-1.5 text-zinc-400 hover:text-white" title="Refresh">
            <RefreshCw className="h-3.5 w-3.5" />
          </button>
        </div>
        {currentCounts ? (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {Object.entries(COLLECTION_LABELS).map(([k, label]) => (
              <div key={k} className="bg-black/40 border border-white/5 p-3">
                <div className="text-[9px] tracking-[0.25em] uppercase text-zinc-500 font-bold">{label}</div>
                <div className="font-display text-2xl mt-1">{currentCounts[k] ?? 0}</div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-zinc-500 text-sm"><Loader2 className="h-4 w-4 animate-spin inline mr-2" /> Loading…</div>
        )}
      </div>

      {/* Export */}
      <div className="bento-card p-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase font-bold text-brand-light mb-2">Step 1 · Export</div>
            <h2 className="font-display text-xl uppercase mb-2">Download this environment's CMS</h2>
            <p className="text-sm text-zinc-400 max-w-md">Saves a JSON file you can upload into another environment. Run this on production after a client makes edits, then import it on preview to mirror their changes.</p>
          </div>
          <button
            onClick={onExport}
            disabled={exporting}
            className="btn-primary disabled:opacity-60"
            data-testid="snapshot-export-button"
          >
            {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
            {exporting ? "Exporting…" : "Download snapshot"}
          </button>
        </div>
      </div>

      {/* Import */}
      <div className="bento-card p-6">
        <div className="text-[10px] tracking-[0.3em] uppercase font-bold text-brand-light mb-2">Step 2 · Import</div>
        <h2 className="font-display text-xl uppercase mb-2">Upload a snapshot</h2>
        <p className="text-sm text-zinc-400 max-w-2xl mb-5">Select a JSON file you exported earlier. Review the import mode, then click Import to apply.</p>

        <div className="flex flex-col sm:flex-row gap-3 items-start">
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            className="btn-ghost"
            data-testid="snapshot-pick-file-button"
          >
            <Upload className="h-4 w-4" /> Choose snapshot file
          </button>
          <input ref={fileRef} type="file" accept="application/json,.json" className="hidden" onChange={onFileChosen} data-testid="snapshot-file-input" />
          {stageName && (
            <div className="text-sm text-zinc-300 flex items-center gap-2 bg-black/40 border border-white/10 px-3 py-2">
              <FileJson className="h-4 w-4 text-brand-light" /> {stageName}
            </div>
          )}
        </div>

        {staged && (
          <>
            <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-3">
              {Object.entries(COLLECTION_LABELS).map(([k, label]) => (
                <div key={k} className="bg-black/40 border border-white/5 p-3">
                  <div className="text-[9px] tracking-[0.25em] uppercase text-zinc-500 font-bold">{label}</div>
                  <div className="font-display text-2xl mt-1">{staged.counts?.[k] ?? 0}</div>
                </div>
              ))}
            </div>
            <div className="mt-5 text-xs text-zinc-500 flex flex-wrap gap-x-5 gap-y-1">
              <span>Source: <span className="text-zinc-300">{staged.source || "—"}</span></span>
              <span>Exported: <span className="text-zinc-300">{staged.exported_at ? new Date(staged.exported_at).toLocaleString() : "—"}</span></span>
              <span>Version: <span className="text-zinc-300">{staged.version}</span></span>
            </div>

            <div className="mt-6">
              <div className="text-[10px] tracking-[0.3em] uppercase font-bold text-brand-light mb-2">Mode</div>
              <div className="flex flex-col gap-2">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input type="radio" name="snapshot-mode" value="replace" checked={mode === "replace"} onChange={() => setMode("replace")} className="mt-1" data-testid="snapshot-mode-replace" />
                  <div>
                    <div className="font-semibold text-sm">Replace</div>
                    <div className="text-xs text-zinc-500">Wipes existing CMS docs in this environment and inserts everything from the snapshot. Mirrors source exactly.</div>
                  </div>
                </label>
                <label className="flex items-start gap-3 cursor-pointer">
                  <input type="radio" name="snapshot-mode" value="merge" checked={mode === "merge"} onChange={() => setMode("merge")} className="mt-1" data-testid="snapshot-mode-merge" />
                  <div>
                    <div className="font-semibold text-sm">Merge</div>
                    <div className="text-xs text-zinc-500">Upserts each item from the snapshot. Anything already in this environment that isn't in the snapshot is left untouched.</div>
                  </div>
                </label>
              </div>
            </div>

            <div className="mt-6 flex items-start gap-3 bg-amber-500/10 border border-amber-500/30 px-4 py-3 text-amber-200 text-xs leading-relaxed">
              <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
              <span>This action overwrites CMS content in <strong>this</strong> environment. Always export a fresh snapshot from this environment first if you might want to revert.</span>
            </div>

            <div className="mt-5 flex flex-wrap gap-3">
              <button
                onClick={onImport}
                disabled={importing}
                className="btn-primary disabled:opacity-60"
                data-testid="snapshot-import-button"
              >
                {importing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                {importing ? "Importing…" : `Import (${mode})`}
              </button>
              <button onClick={() => { setStaged(null); setStageName(""); }} className="btn-ghost">Discard</button>
            </div>
          </>
        )}
      </div>

      {/* Last import result */}
      {lastResult && (
        <div className="bento-card p-6" data-testid="snapshot-last-result">
          <div className="flex items-center gap-2 mb-4">
            <CheckCircle2 className="h-5 w-5 text-brand-light" />
            <div className="font-display uppercase text-lg">Last import</div>
            <span className="ml-2 text-[10px] tracking-[0.25em] uppercase font-bold bg-brand text-white px-2 py-1">{lastResult.mode}</span>
          </div>
          <div className="text-xs text-zinc-500 mb-4">
            Source: <span className="text-zinc-300">{lastResult.source || "—"}</span> ·{" "}
            Imported at: <span className="text-zinc-300">{lastResult.imported_at ? new Date(lastResult.imported_at).toLocaleString() : "—"}</span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {Object.entries(COLLECTION_LABELS).map(([k, label]) => {
              const r = lastResult.result?.[k];
              return (
                <div key={k} className="bg-black/40 border border-white/5 p-3">
                  <div className="text-[9px] tracking-[0.25em] uppercase text-zinc-500 font-bold">{label}</div>
                  <div className="font-display text-2xl mt-1">{r?.count ?? 0}</div>
                  <div className="text-[10px] uppercase tracking-wide text-zinc-500 mt-1">{r?.replaced ? "replaced" : r?.merged ? "merged" : "—"}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};
